import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import emailjs from 'emailjs-com';

// Icons
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';

  // Initialize EmailJS
  React.useEffect(() => {
    emailjs.init('3FLSg56Q8ncAMDj-c');
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: ''
    }
  });

  const onSubmit = (data) => {
    setIsSubmitting(true);

    emailjs.send(
      'service_qm9fbco',
      'template_ttvj76i',
      {
        name: data.name,
        email: data.email,
        phone: data.phone || 'Not provided',
        message: data.message,
        reply_to: data.email,
      }
    )
    .then(() => {
      setIsSubmitting(false);
      setSnackbar({ open: true, message: t('contact.success'), severity: 'success' });
      reset();
    })
    .catch((error) => {
      setIsSubmitting(false);
      setSnackbar({ open: true, message: t('contact.error'), severity: 'error' });
      console.error('Email error:', error);
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const contactInfo = [
    { icon: <LocationOnIcon fontSize="large" />, title: t('contact.address_label'), content: 'Jabotinsky St 33/35, Ramat Gan' },
    { icon: <PhoneIcon fontSize="large" />, title: t('contact.phone_label'), content: '03-511-4482' },
    { icon: <EmailIcon fontSize="large" />, title: t('contact.email_label'), content: 'info@teleline.co.il' },
  ];

  const inputSx = {
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--input-bg)',
      borderRadius: 'var(--radius-lg)',
      color: 'var(--text-primary)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--border-dark)',
    },
    '& .MuiInputLabel-root': {
      color: 'var(--text-secondary)',
    },
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', pt: 8, pb: 12 }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            component="h1"
            variant="h2"
            align="center"
            sx={{ fontWeight: 800, mb: 3, color: 'var(--text-primary)' }}
          >
            {t('contact.title')}
          </Typography>

          <Typography
            variant="h5"
            align="center"
            sx={{
              mb: 8,
              color: 'var(--text-secondary)',
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {t('contact.description')}
          </Typography>
        </motion.div>

        {/* Main Grid */}
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Box
                sx={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: 'var(--shadow-md)',
                  p: { xs: 3, md: 5 },
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary)', mb: 4 }}>
                  {t('contact.send_message')}
                </Typography>

                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Stack spacing={2.5}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t('contact.name')}
                          fullWidth
                          required
                          error={!!errors.name}
                          helperText={errors.name ? 'Name is required' : ''}
                          dir={isRtl ? 'rtl' : 'ltr'}
                          sx={inputSx}
                        />
                      )}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="email"
                          control={control}
                          rules={{
                            required: true,
                            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label={t('contact.email')}
                              fullWidth
                              required
                              error={!!errors.email}
                              helperText={errors.email ?
                                errors.email.type === 'pattern' ? 'Invalid email address' : 'Email is required'
                                : ''}
                              dir="ltr"
                              sx={inputSx}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label={t('contact.phone')}
                              fullWidth
                              dir="ltr"
                              sx={inputSx}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    <Controller
                      name="message"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t('contact.message')}
                          multiline
                          rows={5}
                          fullWidth
                          required
                          error={!!errors.message}
                          helperText={errors.message ? 'Message is required' : ''}
                          dir={isRtl ? 'rtl' : 'ltr'}
                          sx={inputSx}
                        />
                      )}
                    />

                    {/* Submit Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        background: 'linear-gradient(135deg, var(--color-blue) 0%, var(--color-blue-dark) 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '16px 32px',
                        fontSize: '1rem',
                        fontWeight: 700,
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 8px 24px rgba(13, 148, 136, 0.3)',
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <CircularProgress size={20} color="inherit" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <SendIcon fontSize="small" />
                          {t('contact.submit')}
                        </>
                      )}
                    </motion.button>
                  </Stack>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
            >
              <Stack spacing={3} sx={{ h: '100%' }}>
                <Box
                  sx={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-xl)',
                    boxShadow: 'var(--shadow-md)',
                    p: { xs: 3, md: 4 },
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--text-primary)', mb: 4 }}>
                    {t('contact.info')}
                  </Typography>

                  <Stack spacing={3}>
                    {contactInfo.map((info, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1, duration: 0.4 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            flexDirection: isRtl ? 'row-reverse' : 'row',
                            p: 2,
                            background: 'var(--bg-primary)',
                            borderRadius: 'var(--radius-lg)',
                            border: '1px solid var(--border-color)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'var(--border-light)',
                              borderColor: 'var(--color-blue-light)',
                            },
                          }}
                        >
                          <Box sx={{ color: 'var(--color-blue)', mt: 0.5, flexShrink: 0 }}>
                            {info.icon}
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 0.5 }}>
                              {info.title}
                            </Typography>
                            <Typography sx={{ color: 'var(--text-secondary)' }}>
                              {info.content}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
